import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/i18n';
import { Modal, ModalFooter } from '@/components/Shared/Modal';
import { FormField } from '@/components/Shared/FormField';
import { TextArea } from '@/components/Shared/TextArea';
import SearchSelect from '@/components/Shared/SearchSelect';
import type { SearchSelectOption } from '@/components/Shared/SearchSelect';
import { useObjectStore } from '@/store/useObjectStore';
import { useOpenItemsStore } from '@/store/useOpenItemsStore';

const MAX_TITLE = 200;
const MAX_NOTE = 2000;

interface ReportCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Shown on top of the object picker — e.g. the objects of today's assignments. */
  suggestedObjects?: SearchSelectOption[];
}

/**
 * Meldung an die Verwaltung: ein kurzer Text, optional Details und ein Objekt. Landet ohne
 * Zuständigen bei allen Verwaltungs-Usern (ADR-0022, „Meldungen").
 */
export default function ReportCreateModal({ isOpen, onClose, suggestedObjects = [] }: ReportCreateModalProps) {
  const { t } = useTranslation(['openItems', 'common']);
  const createReport = useOpenItemsStore((s) => s.createReport);
  const saving = useOpenItemsStore((s) => s.saving);
  const objects = useObjectStore((s) => s.objects);
  const fetchAllObjects = useObjectStore((s) => s.fetchAllObjects);

  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [objectId, setObjectId] = useState('');

  // Fresh form on every open; preselect the only object of the day if there is exactly one.
  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setNote('');
    setObjectId(suggestedObjects.length === 1 ? suggestedObjects[0].id : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on open, not on every suggestion change
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) fetchAllObjects();
  }, [isOpen, fetchAllObjects]);

  const objectOptions = useMemo(
    () => objects.filter((o) => o.id).map((o) => ({ id: o.id ?? '', label: o.name ?? '' })),
    [objects],
  );

  const canSubmit = title.trim().length > 0 && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const created = await createReport({
      title: title.trim(),
      note: note.trim() || null,
      objectId: objectId || null,
    });
    if (created) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('openItems:report.title')}
      size="md"
      footer={
        <ModalFooter
          onCancel={onClose}
          onConfirm={handleSubmit}
          confirmText={t('openItems:report.submit')}
          cancelText={t('common:actions.cancel')}
          confirmDisabled={!canSubmit}
          loading={saving}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-[12px] text-slate-500">{t('openItems:report.subtitle')}</p>
        <FormField
          label={t('openItems:report.fields.title')}
          name="reportTitle"
          required
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={MAX_TITLE}
          placeholder={t('openItems:report.placeholders.title')}
        />
        <SearchSelect
          label={t('openItems:fields.object')}
          options={objectOptions}
          suggestedOptions={suggestedObjects}
          suggestedLabel={t('openItems:report.suggestedObjects')}
          optionsLabel={t('openItems:report.allObjects')}
          showAllOptions
          inlineDropdown
          value={objectId}
          onChange={setObjectId}
          placeholder={t('openItems:placeholders.object')}
          noResultsText={t('openItems:placeholders.noResults')}
        />
        <TextArea
          label={t('openItems:report.fields.note')}
          name="reportNote"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={MAX_NOTE}
          rows={3}
          placeholder={t('openItems:report.placeholders.note')}
        />
      </div>
    </Modal>
  );
}
